import { Dialog } from '../../../../../../../widgets/dialog';
import { FormControl } from '../../../../../../../widgets/formControl';
import { Input } from '../../../../../../../widgets/input';
import { Link } from '../../../../../../../widgets/buttons';
import { Radio } from '../../../../../../../widgets/radio';
import PreviewOptionImage from 'assets/images/others/configurator_preview.png';
import { flatten } from 'lodash';
import React from 'react';
import { useLazyLoadQuery } from 'react-relay';
import Select from 'react-select';
import { Separator } from 'src/shared/globals';
import { T } from 'src/shared/helpers';
import { useValidationContext, validateRequiredTrimmed, ValidationStateProvider } from 'src/shared/validation';
import styled from 'styled-components';
import {
    ComposerAttributeNode,
    ComposerAttributeType,
    ComposerEcommerceAttribute,
    ComposerEcommercePlatform,
    ComposerEcommerceSettings
} from '../../../../configurator-components/composer-editors/attributes/composerAttributesModels';

import { composerQueriesMerchantProductOptionsQuery } from '../../__generated__/composerQueriesMerchantProductOptionsQuery.graphql';
import ComposerUploadComponent from '../../components/composerUploadComponent';
import { composerMerchantOptionsQuery } from '../../composerQueries';
import { ComposerAttributeNodeAdapter } from '../../helpers/composerNodesAdapter';
import Stack from '../../../../../../../widgets/stack/stack';

const typeSelector = <T,>(type: ComposerAttributeType, choice1: T, choice2: T, choice3: T): T => {
    return type === ComposerAttributeType.Attribute ? choice1 : type === ComposerAttributeType.AttributeGroup ? choice2 : choice3;
};

const ComposerSaveAttributeDialogWindow = styled(Dialog)`
    max-width: 600px;
`;
const Title = styled.h3`
    font-size: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const ComposerSaveAttributeContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    max-height: 600px;
    overflow-y: auto;
    align-items: center;
    padding: 2em;
    justify-content: start;
`;

const ObjectSelectorContainer = styled(Select)`
    min-height: 128px;
`;

interface ComposerSaveAttributeProps {
    onConfirm: (attribute: ComposerAttributeNode | null) => void;
    productCode: string;
    salesChannelID: string;
    sceneCameraList: Zakeke.SceneCameraLocation[];
    meshList?: BABYLON.AbstractMesh[];
    passedAttribute?: ComposerAttributeNode | null;
    fixedType?: ComposerAttributeType | null;
    selectedPlatformAttribute?: ComposerEcommerceAttribute | null;
    ecommerceSettings: ComposerEcommerceSettings | null;
    edit?: boolean;
}

const ComposerSaveAttributePlatformSpecificTooltip = (platform: ComposerEcommercePlatform) => {
    switch (platform) {
        case ComposerEcommercePlatform.Shopify:
            return (
                <>
                    {T._(
                        'We suggest connecting your Zakeke attribute to an option in your Shopify store for a seamless experience. By connecting the two, the values for that option along with the prices are automatically imported, and your customers will see a single product with a list of attributes/options in both the cart and checkout',
                        'Admin'
                    )}
                    <Link
                        href='https://zakeke.zendesk.com/hc/en-us/articles/8669780569500-How-to-match-Configurator-attributes-with-product-options-inside-Shopify'
                        target='_blank'
                        rel='noopener noreferrer'
                    >
                        {T._('Learn more', 'Admin')}
                    </Link>
                </>
            );
        case ComposerEcommercePlatform.BigCommerce:
            return (
                <>
                    {T._(
                        "We suggest connecting your Zakeke attribute to an option or modifier in your BigCommerce. By connecting the two, you ensure that your customers see all the selected options in their cart and that the attributes/options selected are included in the order in BigCommerce. Plus, you'll have the ability to add a specific price for each option. Without connection, these features will be limited. Learn more.",
                        'Admin'
                    )}
                    <Link
                        href='https://zakeke.zendesk.com/hc/en-us/articles/8480109036316/How-to-match-Configurator-attributes-with-options-or-modifiers-in-BigCommerce'
                        target='_blank'
                        rel='noopener noreferrer'
                    >
                        {T._('Learn more', 'Admin')}
                    </Link>
                </>
            );
        default:
            return <></>;
    }
};
const ComposerSaveAttributePreviewContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
`;

const ComposerSaveAttributePreviewOptionContainer = styled.div<{ $isCircle: boolean }>`
    width: 45px;
    height: 45px;
    border: 1px #697a81 solid;
    border-radius: ${(props) => (props.$isCircle ? '50%' : '0')};
    overflow: hidden;

    > img {
        width: 100%;
        height: 100%;
        object-fit: contain;
    }
`;

const ComposerSaveAttributeDialog: React.FC<ComposerSaveAttributeProps> = ({
    onConfirm,
    productCode = '',
    salesChannelID = '',
    edit = false,
    passedAttribute,
    fixedType = null,
    sceneCameraList,
    meshList = [],
    selectedPlatformAttribute = null,
    ecommerceSettings
}) => {
    const validationContext = useValidationContext();
    const fetchedPlatformAttributes = useLazyLoadQuery<composerQueriesMerchantProductOptionsQuery>(composerMerchantOptionsQuery, {
        salesChannelId: salesChannelID,
        sku: productCode
    });
    const [attributeType, setAttributeType] = React.useState<ComposerAttributeType>(passedAttribute ? passedAttribute.type : ComposerAttributeType.Attribute);
    const [parsedAttribute, setParsedAttribute] = React.useState<MPlaza.Attribute | MPlaza.AttributeGroup | MPlaza.AttributeStep>(
        typeSelector<MPlaza.Attribute | MPlaza.AttributeGroup | MPlaza.AttributeStep>(
            attributeType,
            ComposerAttributeNodeAdapter.fromNode(passedAttribute || null),
            ComposerAttributeNodeAdapter.fromGroupNode(passedAttribute || null),
            ComposerAttributeNodeAdapter.fromStepNode(passedAttribute || null)
        )
    );

    const [selectedPlatformAttributeState, setSelectedPlatformAttributeState] = React.useState<{
        label: string;
        value: string;
        selected: boolean;
    } | null>(
        selectedPlatformAttribute
            ? {
                  label: selectedPlatformAttribute.label,
                  value: selectedPlatformAttribute.id,
                  selected: true
              }
            : null
    );

    const platformAttributes = [
        {
            label: '',
            value: '',
            selected: !selectedPlatformAttributeState
        },
        ...fetchedPlatformAttributes.merchantProductOptions.map((attr) => ({
            label: attr.label,
            value: attr.optionID,
            selected: selectedPlatformAttributeState?.value === attr.optionID
        }))
    ];

    const handleConfirm = () => {
        if (!validationContext.submit()) return;

        const nodeAttribute = typeSelector<ComposerAttributeNode | null>(
            attributeType,
            ComposerAttributeNodeAdapter.adjustValue(ComposerAttributeNodeAdapter.toNode(parsedAttribute as MPlaza.Attribute)),
            ComposerAttributeNodeAdapter.toGroupNode(parsedAttribute as MPlaza.AttributeGroup),
            ComposerAttributeNodeAdapter.toStepNode(parsedAttribute as MPlaza.AttributeStep)
        );

        console.log('PARSED ATTRIBUTE', passedAttribute, parsedAttribute, nodeAttribute);
        onConfirm(nodeAttribute);
    };

    const title = edit ? T._('Edit ' + ComposerAttributeType[attributeType], 'Admin') : T._('Create New ' + ComposerAttributeType[attributeType], 'Admin');
    const form = () => {
        let typeSelectElement = (
            <>
                <FormControl label={T._('Type', 'Admin')} tooltipLabelHtml={T._('The type of the attribute that will be created', 'Admin')}>
                    <Select
                        value={{ label: ComposerAttributeType[attributeType], value: attributeType }}
                        onChange={(e) => {
                            const type = (e as { label: string; value: ComposerAttributeType })?.value;
                            if (!type) return;
                            setAttributeType(type);
                            setParsedAttribute(
                                typeSelector<MPlaza.Attribute | MPlaza.AttributeGroup | MPlaza.AttributeStep>(
                                    type,
                                    ComposerAttributeNodeAdapter.fromNode(passedAttribute || null),
                                    ComposerAttributeNodeAdapter.fromGroupNode(passedAttribute || null),
                                    ComposerAttributeNodeAdapter.fromStepNode(passedAttribute || null)
                                )
                            );
                        }}
                        options={Object.values(ComposerAttributeType).map((v) => ({ label: v, value: v }))}
                    />
                </FormControl>
                <Separator />
            </>
        );

        let attributeFormElement = (
            <>
                <FormControl
                    label={T._('Store attribute (optional)', 'Admin')}
                    tooltipLabelHtml={ComposerSaveAttributePlatformSpecificTooltip(ecommerceSettings?.ecommercePlatform || ComposerEcommercePlatform.None)}
                >
                    <Select<{ label: string; value: string }>
                        value={platformAttributes.find((attr) => attr.value === (selectedPlatformAttributeState?.value || ''))}
                        onChange={(selectedOption) => {
                            if (!selectedOption || selectedOption.value === '') {
                                setSelectedPlatformAttributeState(null);
                                (parsedAttribute as MPlaza.Attribute)?.set('attributeCode', '');
                                setParsedAttribute((prev) => prev.clone());
                            } else {
                                setSelectedPlatformAttributeState(platformAttributes.find((attr) => attr.value === selectedOption.value) || null);
                            }
                        }}
                        options={platformAttributes.map((attr) => ({ label: attr.label || T._('None', 'Admin'), value: attr.value }))}
                    />
                </FormControl>
                <Separator />
                <FormControl
                    label={T._('Attribute Name', 'Admin')}
                    tooltipLabelHtml={T._('The name of the attribute that will be displayed in the configurator', 'Admin')}
                >
                    <Input
                        placeholder={T._('Attribute Name', 'Admin')}
                        type='text'
                        value={parsedAttribute?.get('name') || ''}
                        onChange={(e) => {
                            parsedAttribute.set('name', e.target.value);
                            setParsedAttribute((prev) => prev.clone());
                        }}
                        validationKey='composerAttributeName'
                        validationLabel={T._('Attribute Name', 'Admin')}
                        validate={validateRequiredTrimmed}
                        validationMessage={T._('Attribute Name is required.', 'Admin')}
                    />
                </FormControl>
                {!selectedPlatformAttributeState?.value?.trim() && (
                    <FormControl
                        label={T._('Attribute Code', 'Admin')}
                        tooltipLabelHtml={T._('The code of the attribute that will be used in the configurator', 'Admin')}
                    >
                        <Input
                            placeholder={T._('Attribute Code', 'Admin')}
                            type='text'
                            value={(parsedAttribute as MPlaza.Attribute)?.get('attributeCode') || ''}
                            onChange={(e) => {
                                (parsedAttribute as MPlaza.Attribute)?.set('attributeCode', e.target.value);
                                setParsedAttribute((prev) => prev.clone());
                            }}
                        />
                    </FormControl>
                )}
                <Stack>
                    <FormControl
                        label={T._('Attribute Description', 'Admin')}
                        tooltipLabelHtml={T._('The description of the attribute that will be displayed in the configurator as a tooltip', 'Admin')}
                    >
                        <Input
                            placeholder={T._('Attribute Description', 'Admin')}
                            type='text'
                            value={(parsedAttribute as MPlaza.Attribute)?.get('description') || ''}
                            onChange={(e) => {
                                (parsedAttribute as MPlaza.Attribute)?.set('description', e.target.value);
                                setParsedAttribute((prev) => prev.clone());
                            }}
                        />
                    </FormControl>
                    <FormControl
                        label={T._('Preview Options', 'Admin')}
                        tooltipLabelHtml={T._('How the attribute will be displayed in the configurator', 'Admin')}
                    >
                        <ComposerSaveAttributePreviewContainer>
                            {[MPlaza.AttributeOptionPreviewShape.Rectangle, MPlaza.AttributeOptionPreviewShape.Circle].map((shape) => (
                                <div>
                                    <ComposerSaveAttributePreviewOptionContainer $isCircle={shape === MPlaza.AttributeOptionPreviewShape.Circle}>
                                        <img src={PreviewOptionImage} alt='' />
                                    </ComposerSaveAttributePreviewOptionContainer>
                                    <Radio
                                        key={shape}
                                        label={T._(shape === MPlaza.AttributeOptionPreviewShape.Rectangle ? 'Square' : 'Circle', 'Composer')}
                                        name='previewOptions'
                                        value={shape}
                                        checked={(parsedAttribute as MPlaza.Attribute)?.get('optionPreviewShapeID') === shape}
                                        onSelect={() => {
                                            (parsedAttribute as MPlaza.Attribute)?.set('optionPreviewShapeID', shape);
                                            setParsedAttribute((prev) => prev.clone());
                                        }}
                                    ></Radio>
                                </div>
                            ))}
                        </ComposerSaveAttributePreviewContainer>
                    </FormControl>
                    <FormControl
                        label={T._('Options Label', 'Admin')}
                        tooltipLabelHtml={T._('If enabled, the label of the options will be displayed in the configurator', 'Admin')}
                    >
                        <Radio
                            label={T._('Show Options Label', 'Admin')}
                            name='optionsLabel'
                            value='true'
                            checked={(parsedAttribute as MPlaza.Attribute)?.get('hideOptionsLabel') === false}
                            onSelect={() => {
                                (parsedAttribute as MPlaza.Attribute)?.set('hideOptionsLabel', false);
                                setParsedAttribute((prev) => prev.clone());
                            }}
                        />
                        <Radio
                            label={T._('Hide Options Label', 'Admin')}
                            name='optionsLabel'
                            value='false'
                            checked={(parsedAttribute as MPlaza.Attribute)?.get('hideOptionsLabel') === true}
                            onSelect={() => {
                                (parsedAttribute as MPlaza.Attribute)?.set('hideOptionsLabel', true);
                                setParsedAttribute((prev) => prev.clone());
                            }}
                        />
                    </FormControl>
                    <FormControl
                        label={T._('Camera Position', 'Admin')}
                        tooltipLabelHtml={T._('The camera position movement that will be played when the attribute is selected', 'Admin')}
                    >
                        <Select
                            options={sceneCameraList.map((location) => ({
                                label: location.get('name') || T._('Camera Position', 'Admin') + ' ' + location.getLocationID(),
                                value: location.getLocationID()
                            }))}
                            value={{
                                label:
                                    sceneCameraList
                                        .find((location) => location.getLocationID() === (parsedAttribute as MPlaza.Attribute)?.get('sceneCameraLocationID'))
                                        ?.get('name') || '',
                                value: (parsedAttribute as MPlaza.Attribute)?.get('sceneCameraLocationID') || ''
                            }}
                            onChange={(e) => {
                                const selectedValue = e?.value || '';
                                if (selectedValue === '') {
                                    (parsedAttribute as MPlaza.Attribute)?.set('sceneCameraLocationID', null);
                                } else {
                                    (parsedAttribute as MPlaza.Attribute)?.set('sceneCameraLocationID', selectedValue);
                                }
                                setParsedAttribute((prev) => prev.clone());
                            }}
                        ></Select>
                    </FormControl>
                    <FormControl label={T._('Objects', 'Admin')} tooltipLabelHtml={T._('The objects that will be associated with the attribute', 'Admin')}>
                        <ObjectSelectorContainer
                            isMulti={true}
                            value={flatten(
                                (parsedAttribute as MPlaza.Attribute)
                                    ?.get('meshSelector')
                                    ?.split(',')
                                    .map((id) => ({ value: id, label: meshList.find((mesh) => mesh.id === id)?.name ?? undefined })) || []
                            )}
                            options={meshList.map((mesh) => ({
                                value: mesh.id,
                                label: mesh.name || T._('Mesh', 'Admin')
                            }))}
                            onChange={(items) => {
                                console.log('Selected items', items, parsedAttribute);
                                (parsedAttribute as MPlaza.Attribute)?.set(
                                    'meshSelector',
                                    (items as any).map((item: { value: string; label: string }) => item.value).join(';')
                                );
                                setParsedAttribute((prev) => prev.clone());
                            }}
                            menuPlacement='top'
                        />
                        {/* <ObjectSelectorContainer
						multiple
						value={(parsedAttribute as MPlaza.Attribute)?.get('meshSelector') || []}
						onChange={(e) => {
							const selectedValue = e;
							if (selectedValue === '') {
								(parsedAttribute as MPlaza.Attribute)?.set('meshSelector', null);
							} else {
								(parsedAttribute as MPlaza.Attribute)?.set('meshSelector', selectedValue);
							}
							setParsedAttribute((prev) => prev.clone());
						}}
					>
						<option value=''></option>
						{meshList.map((mesh) => (
							<option key={mesh.id} value={mesh.id}>
								{mesh.name || T._('Mesh', 'Admin') + ' ' + mesh.id}
							</option>
						))}
					</ObjectSelectorContainer> */}
                    </FormControl>
                </Stack>
            </>
        );
        //name, icon, attributes direction, camera position, objects
        let attributeGroupFormElement = (
            <>
                <Stack>
                    <FormControl label={T._('Group Image', 'Admin')}>
                        <ComposerUploadComponent
                            onImagePick={(imageUrl) => {
                                (parsedAttribute as MPlaza.AttributeGroup)?.set('imageUrl', imageUrl);
                                setParsedAttribute((prev) => prev.clone());
                            }}
                        />
                    </FormControl>
                    <FormControl
                        label={T._('Group Name', 'Admin')}
                        tooltipLabelHtml={T._('The name of the group that will be displayed in the configurator', 'Admin')}
                    >
                        <Input
                            placeholder={T._('Group Name', 'Admin')}
                            type='text'
                            value={parsedAttribute?.get('name') || ''}
                            onChange={(e) => {
                                parsedAttribute.set('name', e.target.value);
                                setParsedAttribute((prev) => prev.clone());
                            }}
                            validationKey='composerAttributeGroupName'
                            validationLabel={T._('Group Name', 'Admin')}
                            validate={validateRequiredTrimmed}
                            validationMessage={T._('Group Name is required.', 'Admin')}
                        />
                    </FormControl>
                    <FormControl
                        label={T._('Group Icon', 'Admin')}
                        tooltipLabelHtml={T._('The icon of the group that will be displayed in the configurator', 'Admin')}
                    >
                        <Input
                            placeholder={T._('Group Icon', 'Admin')}
                            type='text'
                            value={(parsedAttribute as MPlaza.AttributeGroup)?.get('imageUrl') || ''}
                            onChange={(e) => {
                                (parsedAttribute as MPlaza.AttributeGroup)?.set('imageUrl', e.target.value);
                                setParsedAttribute((prev) => prev.clone());
                            }}
                        />
                    </FormControl>
                    <FormControl
                        label={T._('Attributes Direction', 'Admin')}
                        tooltipLabelHtml={T._(
                            'The direction of the attributes that will be displayed in the configurator. If horizontal, the attributes will be displayed in a row, if vertical, they will be displayed in a column (No effect on mobile devices)',
                            'Admin'
                        )}
                    >
                        <Radio
                            label={T._('Horizontal', 'Admin')}
                            name='attributesDirection'
                            value={MPlaza.AttributeGroupDirection.Horizontal}
                            checked={(parsedAttribute as MPlaza.AttributeGroup)?.get('direction') === MPlaza.AttributeGroupDirection.Horizontal}
                            onSelect={() => {
                                (parsedAttribute as MPlaza.AttributeGroup)?.set('direction', MPlaza.AttributeGroupDirection.Horizontal);
                                setParsedAttribute((prev) => prev.clone());
                            }}
                        />
                        <Radio
                            label={T._('Vertical', 'Admin')}
                            name='attributesDirection'
                            value={MPlaza.AttributeGroupDirection.Vertical}
                            checked={(parsedAttribute as MPlaza.AttributeGroup)?.get('direction') === MPlaza.AttributeGroupDirection.Vertical}
                            onSelect={() => {
                                (parsedAttribute as MPlaza.AttributeGroup)?.set('direction', MPlaza.AttributeGroupDirection.Vertical);
                                setParsedAttribute((prev) => prev.clone());
                            }}
                        />
                    </FormControl>
                </Stack>
                {(parsedAttribute as MPlaza.AttributeGroup)?.get('direction') === MPlaza.AttributeGroupDirection.Vertical && (
                    <FormControl
                        label={T._('Attributes State', 'Admin')}
                        tooltipLabelHtml={T._("Choose how to show and open the attributes (don't have effect in mobile version)", 'Admin')}
                    >
                        <Radio
                            label={T._('First open, others with accordion', 'Admin')}
                            name='attributesAlwaysOpened'
                            value='true'
                            checked={!(parsedAttribute as MPlaza.AttributeGroup)?.get('attributesAlwaysOpened')}
                            onSelect={() => {
                                (parsedAttribute as MPlaza.AttributeGroup)?.set('attributesAlwaysOpened', false);
                                setParsedAttribute((prev) => prev.clone());
                            }}
                        />
                        <Radio
                            label={T._('all attributes opened', 'Admin')}
                            name='attributesAlwaysOpened'
                            value='false'
                            checked={!!(parsedAttribute as MPlaza.AttributeGroup)?.get('attributesAlwaysOpened')}
                            onSelect={() => {
                                (parsedAttribute as MPlaza.AttributeGroup)?.set('attributesAlwaysOpened', true);
                                setParsedAttribute((prev) => prev.clone());
                            }}
                        />
                    </FormControl>
                )}
                <Stack>
                    <FormControl
                        label={T._('Camera Position', 'Admin')}
                        tooltipLabelHtml={T._('The camera position movement that will be played when the group is selected', 'Admin')}
                    >
                        <Select
                            options={sceneCameraList.map((location) => ({
                                label: location.get('name') || T._('Camera Position', 'Admin') + ' ' + location.getLocationID(),
                                value: location.getLocationID()
                            }))}
                            value={{
                                label:
                                    sceneCameraList
                                        .find(
                                            (location) => location.getLocationID() === (parsedAttribute as MPlaza.AttributeGroup)?.get('sceneCameraLocationID')
                                        )
                                        ?.get('name') || T._('Camera Position', 'Admin'),
                                value: (parsedAttribute as MPlaza.AttributeGroup)?.get('sceneCameraLocationID') || ''
                            }}
                            onChange={(e) => {
                                const selectedValue = e?.value || '';
                                if (selectedValue === '') {
                                    (parsedAttribute as MPlaza.AttributeGroup)?.set('sceneCameraLocationID', null);
                                } else {
                                    (parsedAttribute as MPlaza.AttributeGroup)?.set('sceneCameraLocationID', selectedValue);
                                }
                                setParsedAttribute((prev) => prev.clone());
                            }}
                        ></Select>
                    </FormControl>
                    <FormControl label={T._('Objects', 'Admin')} tooltipLabelHtml={T._('The objects that will be associated with the group', 'Admin')}>
                        <ObjectSelectorContainer
                            isMulti
                            value={flatten(
                                (parsedAttribute as MPlaza.AttributeGroup)
                                    ?.get('meshSelector')
                                    ?.split(',')
                                    .map((id) => ({ value: id, label: meshList.find((mesh) => mesh.id === id)?.name ?? undefined })) || []
                            )}
                            options={meshList.map((mesh) => ({
                                value: mesh.id,
                                label: mesh.name || T._('Mesh', 'Admin')
                            }))}
                            onChange={(items) => {
                                console.log('Selected items', items, parsedAttribute);
                                (parsedAttribute as MPlaza.Attribute)?.set(
                                    'meshSelector',
                                    (items as any).map((item: { value: string; label: string }) => item.value).join(';')
                                );
                                setParsedAttribute((prev) => prev.clone());
                            }}
                            menuPlacement='top'
                        />
                    </FormControl>
                </Stack>
            </>
        );
        //name, camera position
        let attributeStepFormElement = (
            <>
                <Stack>
                    <FormControl
                        label={T._('Step Name', 'Admin')}
                        tooltipLabelHtml={T._('The name of the step that will be displayed in the configurator', 'Admin')}
                    >
                        <Input
                            placeholder={T._('Step Name', 'Admin')}
                            type='text'
                            value={parsedAttribute?.get('name') || ''}
                            onChange={(e) => {
                                parsedAttribute.set('name', e.target.value);
                                setParsedAttribute((prev) => prev.clone());
                            }}
                            validationKey='composerAttributeStepName'
                            validationLabel={T._('Step Name', 'Admin')}
                            validate={validateRequiredTrimmed}
                            validationMessage={T._('Step Name is required.', 'Admin')}
                        />
                    </FormControl>
                    <FormControl
                        label={T._('Camera Position', 'Admin')}
                        tooltipLabelHtml={T._('The camera position movement that will be played when the step is selected', 'Admin')}
                    >
                        <Select
                            options={sceneCameraList.map((location) => ({
                                label: location.get('name') || T._('Camera Position', 'Admin') + ' ' + location.getLocationID(),
                                value: location.getLocationID()
                            }))}
                            value={{
                                label:
                                    sceneCameraList
                                        .find(
                                            (location) => location.getLocationID() === (parsedAttribute as MPlaza.AttributeGroup)?.get('sceneCameraLocationID')
                                        )
                                        ?.get('name') || T._('Camera Position', 'Admin'),
                                value: (parsedAttribute as MPlaza.AttributeGroup)?.get('sceneCameraLocationID') || ''
                            }}
                            onChange={(e) => {
                                const selectedValue = e?.value || '';
                                if (selectedValue === '') {
                                    (parsedAttribute as MPlaza.AttributeGroup)?.set('sceneCameraLocationID', null);
                                } else {
                                    (parsedAttribute as MPlaza.AttributeGroup)?.set('sceneCameraLocationID', selectedValue);
                                }
                                setParsedAttribute((prev) => prev.clone());
                            }}
                        ></Select>
                    </FormControl>
                </Stack>
            </>
        );
        if (fixedType !== null) {
            return typeSelector(fixedType, attributeFormElement, attributeGroupFormElement, attributeStepFormElement);
        } else {
            return [typeSelectElement, typeSelector(attributeType, attributeFormElement, attributeGroupFormElement, attributeStepFormElement)];
        }
    };

    return (
        <Dialog windowDecorator={ComposerSaveAttributeDialogWindow} buttons={[{ label: T._('Confirm', 'Admin'), onClick: handleConfirm }]}>
            <Title>{title}</Title>
            <ComposerSaveAttributeContainer>
                <div>
                    {T._('Do you need help?', 'Admin')}{' '}
                    <Link
                        href='https://zakeke.zendesk.com/hc/en-us/articles/12934839221020-Attributes-Options-and-Actions#h_01G0MHCK9C0KKE90D04WE4SVM4'
                        target='_blank'
                        rel='noopener noreferrer'
                    >
                        {T._('Read here', 'Admin')}
                    </Link>
                </div>
                {form()}
            </ComposerSaveAttributeContainer>
        </Dialog>
    );
};

const ComposerSaveAttributeDialogWithValidation: React.FC<ComposerSaveAttributeProps> = (props) => (
    <ValidationStateProvider>
        <ComposerSaveAttributeDialog {...props} />
    </ValidationStateProvider>
);

export default ComposerSaveAttributeDialogWithValidation;
